import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatMoney, CURRENCIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { ContactCombobox } from "../components/ContactCombobox";

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const base = user.organization?.base_currency || "NGN";
  const empty = { direction: "in", amount: 0, currency: base, method: "cash", reference: "", date: new Date().toISOString().slice(0, 10), note: "", payer_id: null, purpose: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => setItems((await api.get("/payments")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/payments", form); toast.success("Payment recorded"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete payment?")) return; await api.delete(`/payments/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments" subtitle="Money in & money out"
        action={<Button className="rounded-none gap-2" data-testid="create-payment-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus className="w-4 h-4" /> New Payment</Button>} />

      {items.length === 0 ? <EmptyState title="No payments recorded" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Direction</TableHead><TableHead>Who</TableHead><TableHead>Purpose</TableHead><TableHead>Method</TableHead>
              <TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} className="hover:bg-secondary/60" data-testid="payment-row">
                  <TableCell className="font-mono text-sm">{p.date}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs font-mono font-semibold uppercase px-2 py-0.5 ${p.direction === "in" ? "bg-success text-white" : "bg-destructive text-white"}`}>
                      {p.direction === "in" ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}{p.direction === "in" ? "IN" : "OUT"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{p.payer_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.purpose || "—"}</TableCell>
                  <TableCell className="capitalize">{p.method}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.reference || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{formatMoney(p.amount, p.currency)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">New Payment</DialogTitle><DialogDescription>Record money received or paid out.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="payment-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in">Money In</SelectItem><SelectItem value="out">Money Out</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Amount</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value || 0) }))} data-testid="payment-amount-input" /></div>
            <div><Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Reference</Label><Input className="rounded-none mt-1" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} placeholder="INV-1001" /></div>
            <div className="sm:col-span-2"><Label>{form.direction === "in" ? "Who pays (customer)" : "Paid to (supplier)"}</Label>
              <ContactCombobox type={form.direction === "in" ? "customer" : "supplier"} value={form.payer_id} onValueChange={(id) => setForm((f) => ({ ...f, payer_id: id }))} placeholder="Search or add a contact…" testId="payment-payer" />
            </div>
            <div className="sm:col-span-2"><Label>What they paid for</Label><Input className="rounded-none mt-1" value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Invoice INV-1001 deposit" data-testid="payment-purpose-input" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Input className="rounded-none mt-1" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <DialogFooter className="sm:col-span-2"><Button type="submit" className="rounded-none" data-testid="save-payment-btn">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
